/**
 * Enhanced Location Map Component
 * 
 * Extended version of LocationMap with support for:
 * - Multiple markers (users + proposed locations)
 * - Route lines between points
 * - Real-time location updates
 * - Custom marker colors
 */

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap, AttributionControl } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default icon issue
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

const DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Custom colored icons
const createColoredIcon = (color: string) => {
  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="
      width: 30px;
      height: 30px;
      background-color: ${color};
      border: 3px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 15],
    popupAnchor: [0, -15]
  });
};

// Avatar icon for user markers
const createAvatarIcon = (name: string, photoURL?: string, borderColor: string = '#3B82F6') => {
  const initials = name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  const bgColor = borderColor === '#3B82F6' ? '#DBEAFE' : '#FED7AA';
  const textColor = borderColor === '#3B82F6' ? '#1D4ED8' : '#C2410C';
  
  const innerContent = photoURL
    ? `<img src="${photoURL}" style="width:100%;height:100%;object-fit:cover;border-radius:50%;" onerror="this.style.display='none';this.nextSibling.style.display='flex'" /><div style="display:none;width:100%;height:100%;align-items:center;justify-content:center;font-size:12px;font-weight:700;color:${textColor};background:${bgColor};border-radius:50%;">${initials}</div>`
    : `<div style="width:100%;height:100%;display:flex;align-items:center;justify-content:center;font-size:12px;font-weight:700;color:${textColor};background:${bgColor};border-radius:50%;">${initials}</div>`;

  return L.divIcon({
    className: 'custom-avatar-marker',
    html: `<div style="
      width: 36px;
      height: 36px;
      border: 3px solid ${borderColor};
      border-radius: 50%;
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
      overflow: hidden;
      background: white;
    ">${innerContent}</div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 18],
    popupAnchor: [0, -18]
  });
};

interface UserLocation {
  userId: string;
  userName: string;
  lat: number;
  lng: number;
  address?: string;
  photoURL?: string;
  locationAccuracy?: 'high' | 'medium' | 'low' | 'unknown';
}

interface ProposedLocation {
  id: string;
  lat: number;
  lng: number;
  address: string;
  name?: string;
  matchQuality?: 'best' | 'good' | 'not_efficient';
  user1Distance?: number;
  user2Distance?: number;
  user1TravelTime?: number;
  user2TravelTime?: number;
  isFavorite?: boolean;
  isMutual?: boolean;
  proposedBy?: 'user1' | 'user2';
  draggable?: boolean;
}

interface RouteLine {
  from: { lat: number; lng: number };
  to: { lat: number; lng: number };
  color: string;
  label?: string;
  /** GeoJSON coordinates [lng, lat][] from OSRM — renders as road-following polyline */
  geometry?: Array<[number, number]>;
}

interface EnhancedLocationMapProps {
  /** User locations to display */
  userLocations?: UserLocation[];
  /** Proposed locations to display */
  proposedLocations?: ProposedLocation[];
  /** Selected location (if any) */
  selectedLocation?: { lat: number; lng: number; address: string };
  /** Route lines to draw */
  routeLines?: RouteLine[];
  /** Map height in pixels */
  height?: number;
  /** Zoom level */
  zoom?: number;
  /** Show loading state */
  isLoading?: boolean;
  /** Auto-fit bounds to show all markers */
  autoFitBounds?: boolean;
  /** Callback when a draggable marker is moved */
  onLocationDragged?: (id: string, lat: number, lng: number) => void;
  /**
   * userId to "follow" — when set, the map pans smoothly to keep that user's
   * marker in view as their GPS position updates (navigation mode).
   */
  followUserId?: string;
}

// Component to auto-fit map bounds (only on initial mount, not on every update)
function FitBounds({ bounds }: { bounds: L.LatLngBounds }) {
  const map = useMap();
  const hasFitted = React.useRef(false);
  
  useEffect(() => {
    if (bounds.isValid() && !hasFitted.current) {
      // Guard: only act if map container is still in the DOM
      if (!map.getContainer().isConnected) return;
      map.fitBounds(bounds, { padding: [50, 50] });
      hasFitted.current = true;
    }
  }, [map, bounds]);
  
  return null;
}

// Smoothly pan the map to keep the followed user's marker centred as GPS updates
function FollowUser({ userId, userLocations }: { userId: string; userLocations: UserLocation[] }) {
  const map = useMap();
  const target = userLocations.find(u => u.userId === userId);
  const prevLatLng = React.useRef<[number, number] | null>(null);

  useEffect(() => {
    if (!target) return;
    // Guard: only act if map container is still mounted
    if (!map.getContainer().isConnected) return;
    const next: [number, number] = [target.lat, target.lng];
    const prev = prevLatLng.current;
    // Only pan if position actually changed (avoids jarring re-renders on prop updates)
    if (!prev || Math.abs(prev[0] - next[0]) > 0.0001 || Math.abs(prev[1] - next[1]) > 0.0001) {
      map.panTo(next, { animate: true, duration: 1.2 });
      prevLatLng.current = next;
    }
  }, [map, target]);

  return null;
}

/** Returns true when lat and lng are finite, non-null numbers */
const isValidCoord = (lat: unknown, lng: unknown): lat is number =>
  typeof lat === 'number' && typeof lng === 'number' &&
  isFinite(lat) && isFinite(lng);

const EnhancedLocationMap: React.FC<EnhancedLocationMapProps> = ({
  userLocations = [],
  proposedLocations = [],
  selectedLocation,
  followUserId,
  routeLines = [],
  height = 400,
  zoom = 13,
  isLoading = false,
  autoFitBounds = true,
  onLocationDragged
}) => {
  const [isMapReady, setIsMapReady] = useState(false);
  
  // Filter out any entries that arrived without valid coordinates (e.g. mid-update)
  const validUserLocations = useMemo(
    () => userLocations.filter(u => isValidCoord(u.lat, u.lng)),
    [userLocations]
  );
  const validProposedLocations = useMemo(
    () => proposedLocations.filter(l => isValidCoord(l.lat, l.lng)),
    [proposedLocations]
  );
  const validSelectedLocation = selectedLocation && isValidCoord(selectedLocation.lat, selectedLocation.lng)
    ? selectedLocation
    : undefined;

  // Calculate center point (only used for initial map render, stable ref)
  const initialCenter = useRef<[number, number] | null>(null);
  
  const center = useMemo((): [number, number] => {
    const allPoints: Array<{ lat: number; lng: number }> = [];
    
    if (validSelectedLocation) {
      allPoints.push(validSelectedLocation);
    }
    
    validUserLocations.forEach(user => {
      allPoints.push({ lat: user.lat, lng: user.lng });
    });
    
    validProposedLocations.forEach(loc => {
      allPoints.push({ lat: loc.lat, lng: loc.lng });
    });
    
    if (allPoints.length === 0) {
      return [52.1326, 5.2913]; // Default: Netherlands center
    }
    
    const avgLat = allPoints.reduce((sum, p) => sum + p.lat, 0) / allPoints.length;
    const avgLng = allPoints.reduce((sum, p) => sum + p.lng, 0) / allPoints.length;
    
    return [avgLat, avgLng];
  }, [validSelectedLocation, validUserLocations, validProposedLocations]);
  
  // Lock initial center so MapContainer doesn't shift on re-renders
  if (!initialCenter.current) {
    initialCenter.current = center;
  }
  
  // Calculate bounds for auto-fit
  const bounds = useMemo((): L.LatLngBounds => {
    const b = new L.LatLngBounds([]);
    
    if (validSelectedLocation) {
      b.extend([validSelectedLocation.lat, validSelectedLocation.lng]);
    }
    
    validUserLocations.forEach(user => {
      b.extend([user.lat, user.lng]);
    });
    
    validProposedLocations.forEach(loc => {
      b.extend([loc.lat, loc.lng]);
    });
    
    return b;
  }, [validSelectedLocation, validUserLocations, validProposedLocations]);
  
  // Show loading state
  if (isLoading) {
    return (
      <div 
        className="w-full bg-gray-100 rounded-lg flex items-center justify-center"
        style={{ height }}
      >
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600 mx-auto mb-2"></div>
          <p className="text-gray-600 text-sm">Kaart laden...</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="w-full rounded-lg overflow-hidden shadow-md border border-gray-200 isolate">
      <MapContainer
        center={initialCenter.current || center}
        zoom={zoom}
        style={{ height, width: '100%' }}
        scrollWheelZoom={true}
        whenReady={() => setIsMapReady(true)}
        attributionControl={false}
      >
        {/* OpenStreetMap tiles — attribution zonder "Leaflet" prefix */}
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          maxZoom={19}
        />
        <AttributionControl prefix={false} />
        
        {/* Auto-fit bounds (only when not following a user, to avoid fighting FlyTo) */}
        {autoFitBounds && !followUserId && bounds.isValid() && <FitBounds bounds={bounds} />}

        {/* Follow a specific user's GPS position during navigation */}
        {followUserId && <FollowUser userId={followUserId} userLocations={validUserLocations} />}
        
        {/* User location markers with avatars */}
        {validUserLocations.map((user, index) => {
          const borderColor = index === 0 ? '#3B82F6' : '#F97316';
          const avatarIcon = createAvatarIcon(user.userName, user.photoURL, borderColor);
          const isLowAccuracy = user.locationAccuracy === 'low' || user.locationAccuracy === 'unknown';
          
          return (
            <Marker
              key={`user-${user.userId}`}
              position={[user.lat, user.lng]}
              icon={avatarIcon}
            >
              <Popup>
                <div className="text-sm">
                  <p className="font-semibold mb-1">{user.userName}</p>
                  <p className="text-gray-600 text-xs">{user.address || 'Locatie'}</p>
                  {isLowAccuracy && (
                    <p className="text-amber-600 text-xs mt-1 font-medium">⚠ Locatie mogelijk onnauwkeurig (VPN/incognito)</p>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}
        
        {/* Proposed location markers */}
        {validProposedLocations.map((loc) => {
          // Color logic: mutual > favorite > match quality > default by owner
          let iconColor = loc.proposedBy === 'user1' ? '#3B82F6' : '#F97316'; // Blue for user1, Orange for user2
          let iconLabel = loc.name?.charAt(0)?.toUpperCase() || '📍';
          let borderStyle = '';
          
          if (loc.isMutual) {
            iconColor = '#10B981'; // Green for mutual match
            iconLabel = '♥';
            borderStyle = '3px solid #059669';
          } else if (loc.isFavorite) {
            iconColor = '#8B5CF6'; // Purple for favorited
            iconLabel = '♡';
            borderStyle = '3px solid #7C3AED';
          } else if (loc.matchQuality === 'best') {
            iconColor = '#10B981';
            borderStyle = '3px solid #059669';
          } else if (loc.matchQuality === 'good') {
            iconColor = '#F59E0B';
            borderStyle = '3px solid #D97706';
          } else if (loc.matchQuality === 'not_efficient') {
            iconColor = '#EF4444';
            borderStyle = '3px solid #DC2626';
          } else {
            borderStyle = `3px solid ${iconColor}`;
          }
          
          const proposalIcon = L.divIcon({
            className: 'custom-proposal-marker',
            html: `<div style="
              width: 28px;
              height: 28px;
              background: ${iconColor};
              border: ${borderStyle};
              border-radius: 50%;
              box-shadow: 0 2px 8px rgba(0,0,0,0.25);
              display: flex;
              align-items: center;
              justify-content: center;
              color: white;
              font-size: 12px;
              font-weight: 700;
              cursor: ${loc.draggable ? 'grab' : 'pointer'};
              ${loc.draggable ? 'outline: 2px dashed rgba(255,255,255,0.6); outline-offset: 2px;' : ''}
            ">${iconLabel}</div>`,
            iconSize: [28, 28],
            iconAnchor: [14, 14],
            popupAnchor: [0, -14]
          });
          
          return (
            <Marker
              key={`proposal-${loc.id}`}
              position={[loc.lat, loc.lng]}
              icon={proposalIcon}
              draggable={loc.draggable || false}
              eventHandlers={loc.draggable && onLocationDragged ? {
                dragend: (e: any) => {
                  const marker = e.target;
                  const pos = marker.getLatLng();
                  onLocationDragged(loc.id, pos.lat, pos.lng);
                }
              } : {}}
            >
              <Popup>
                <div className="text-sm">
                  <div className="flex items-center gap-1.5 mb-1">
                    <span className="font-semibold">{loc.name || loc.address}</span>
                    {loc.isMutual && <span style={{color:'#10B981', fontWeight:700, fontSize:'11px'}}>♥ Match</span>}
                    {loc.isFavorite && !loc.isMutual && <span style={{color:'#8B5CF6', fontWeight:700, fontSize:'11px'}}>♡ Favoriet</span>}
                  </div>
                  <p className="text-gray-600 text-xs">{loc.address}</p>
                  {loc.draggable && <p className="text-blue-600 text-xs mt-1 font-medium">↕ Versleep om locatie aan te passen</p>}
                  {loc.user1Distance !== undefined && loc.user2Distance !== undefined && (
                    <div className="mt-2 text-xs">
                      <p>User 1: {loc.user1Distance.toFixed(1)}km ({loc.user1TravelTime} min)</p>
                      <p>User 2: {loc.user2Distance.toFixed(1)}km ({loc.user2TravelTime} min)</p>
                      <p className="font-medium mt-1">
                        Totaal: {(loc.user1Distance + loc.user2Distance).toFixed(1)}km
                      </p>
                    </div>
                  )}
                </div>
              </Popup>
            </Marker>
          );
        })}
        
        {/* Selected location marker */}
        {validSelectedLocation && (
          <Marker position={[validSelectedLocation.lat, validSelectedLocation.lng]}>
            <Popup>
              <div className="text-sm">
                <p className="font-medium mb-1">✅ Geselecteerde locatie</p>
                <p className="text-gray-600">{validSelectedLocation.address}</p>
              </div>
            </Popup>
          </Marker>
        )}
        
        {/* Route lines — use road geometry from OSRM when available, else straight line */}
        {routeLines.map((line, index) => {
          const positions: [number, number][] = line.geometry && line.geometry.length > 1
            // OSRM geometry is [lng, lat] — Leaflet wants [lat, lng]
            ? line.geometry.map(([lng, lat]) => [lat, lng])
            : [[line.from.lat, line.from.lng], [line.to.lat, line.to.lng]];

          return (
            <Polyline
              key={`route-${index}`}
              positions={positions}
              color={line.color}
              weight={4}
              opacity={0.75}
            />
          );
        })}
      </MapContainer>
      
      {/* Map footer legend is intentionally omitted here — 
           the parent component provides its own external legend for full control over names and styling */}
    </div>
  );
};

export default EnhancedLocationMap;
